import React from 'react';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const WorkingCapitalSection = ({ formData, handleInputChange, errors }) => {
  const calculateWorkingCapital = () => {
    const currentAssets = parseFloat(formData?.currentAssets) || 0;
    const currentLiabilities = parseFloat(formData?.currentLiabilities) || 0;
    return (currentAssets - currentLiabilities)?.toFixed(0);
  };

  const calculateCurrentRatio = () => {
    const currentAssets = parseFloat(formData?.currentAssets) || 0;
    const currentLiabilities = parseFloat(formData?.currentLiabilities) || 0;
    if (currentLiabilities === 0) return 0;
    return (currentAssets / currentLiabilities)?.toFixed(2);
  };

  const calculateCashReserveDays = () => {
    const cashReserve = parseFloat(formData?.cashReserve) || 0;
    const dailyOperatingCost = parseFloat(formData?.dailyOperatingCost) || 0;
    if (dailyOperatingCost === 0) return 0;
    return (cashReserve / dailyOperatingCost)?.toFixed(0);
  };

  const getRatioStatus = (ratio) => {
    if (ratio >= 2) return { color: 'success', label: 'Sehat', icon: 'CheckCircle2' };
    if (ratio >= 1) return { color: 'warning', label: 'Cukup', icon: 'AlertCircle' };
    return { color: 'error', label: 'Berisiko', icon: 'XCircle' };
  };

  const currentRatio = parseFloat(calculateCurrentRatio());
  const ratioStatus = getRatioStatus(currentRatio);

  return (
    <div className="card">
      <div className="flex items-start gap-4 mb-6">
        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Icon name="Wallet" size={24} color="var(--color-primary)" />
        </div>
        <div className="flex-1">
          <h2 className="text-2xl md:text-3xl font-semibold mb-2">Modal Kerja & Likuiditas</h2>
          <p className="text-sm md:text-base text-muted-foreground">
            Analisis kemampuan keuangan untuk memenuhi kewajiban jangka pendek
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6">
        <Input
          label="Aset Lancar"
          type="number"
          placeholder="0"
          description="Kas, piutang, persediaan"
          value={formData?.currentAssets || ''}
          onChange={(e) => handleInputChange('currentAssets', null, e?.target?.value)}
          error={errors?.currentAssets}
          required
        />

        <Input
          label="Kewajiban Lancar"
          type="number"
          placeholder="0"
          description="Hutang jangka pendek"
          value={formData?.currentLiabilities || ''}
          onChange={(e) => handleInputChange('currentLiabilities', null, e?.target?.value)}
          error={errors?.currentLiabilities}
          required
        />

        <Input
          label="Cadangan Kas"
          type="number"
          placeholder="0"
          description="Dana tunai tersedia"
          value={formData?.cashReserve || ''}
          onChange={(e) => handleInputChange('cashReserve', null, e?.target?.value)}
          error={errors?.cashReserve}
          required
        />

        <Input
          label="Biaya Operasional Harian"
          type="number"
          placeholder="0"
          description="Rata-rata pengeluaran per hari"
          value={formData?.dailyOperatingCost || ''}
          onChange={(e) => handleInputChange('dailyOperatingCost', null, e?.target?.value)}
          error={errors?.dailyOperatingCost}
          required
        />

        <Input
          label="Persediaan Bahan Baku"
          type="number"
          placeholder="0"
          description="Nilai persediaan dalam Rupiah"
          value={formData?.rawMaterialInventory || ''}
          onChange={(e) => handleInputChange('rawMaterialInventory', null, e?.target?.value)}
          error={errors?.rawMaterialInventory}
        />

        <Input
          label="Persediaan Produk Jadi"
          type="number"
          placeholder="0"
          description="Nilai produk siap jual"
          value={formData?.finishedGoodsInventory || ''}
          onChange={(e) => handleInputChange('finishedGoodsInventory', null, e?.target?.value)}
          error={errors?.finishedGoodsInventory}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 md:p-6 bg-muted rounded-lg">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="TrendingUp" size={20} color="var(--color-primary)" />
            <p className="text-sm text-muted-foreground">Modal Kerja</p>
          </div>
          <p className="text-xl md:text-2xl font-bold data-text">
            Rp {parseFloat(calculateWorkingCapital())?.toLocaleString('id-ID')}
          </p>
        </div>

        <div className={`p-4 md:p-6 bg-${ratioStatus?.color}/10 rounded-lg border-2 border-${ratioStatus?.color}/20`}>
          <div className="flex items-center gap-3 mb-2">
            <Icon name={ratioStatus?.icon} size={20} color={`var(--color-${ratioStatus?.color})`} />
            <p className="text-sm text-muted-foreground">Rasio Lancar</p>
          </div>
          <div className="flex items-baseline gap-2">
            <p className="text-xl md:text-2xl font-bold data-text">{currentRatio}</p>
            <span className={`text-sm font-medium text-${ratioStatus?.color}`}>{ratioStatus?.label}</span>
          </div>
        </div>

        <div className="p-4 md:p-6 bg-muted rounded-lg">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="Clock" size={20} color="var(--color-accent)" />
            <p className="text-sm text-muted-foreground">Cadangan Kas</p>
          </div>
          <p className="text-xl md:text-2xl font-bold data-text">
            {calculateCashReserveDays()} Hari
          </p>
        </div>
      </div>
      <div className="mt-6 p-4 bg-muted/50 rounded-lg border border-border">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium mb-1">Panduan Rasio Lancar:</p>
            <p>&gt; 2.0 = Likuiditas Sehat | 1.0-2.0 = Cukup | &lt; 1.0 = Berisiko Tinggi</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkingCapitalSection;